// Layout Template — CRUD-able replacement for the old Slot.kind enum.
//
// Shape: a v1 tree of Container/Row/Column nodes (same primitives as
// page-layout v3) with `ItemPlaceholderNode` as the leaf (instead of
// SlotNode). Each placeholder marks where an item from the slot's group
// will render. Public renderer walks the tree, assigns items in tree
// order, and applies the per-placeholder `variant` string as a CSS hint.

import type { ColumnSpan, ContainerVariant, RowGap } from './page-layout';
import type { HydratedGroupItem } from './group-content';

/** Template containers can additionally be `'carousel'` — root-only variant
 *  that flips the template into a slider. Page-layout containers (slot
 *  parents) stay limited to `'fluid' | 'fixed'`. */
export type TemplateContainerVariant = ContainerVariant | 'carousel';

export interface TemplateBaseNode {
  id: string;
}

export interface TemplateContainerNode extends TemplateBaseNode {
  type: 'container';
  variant: TemplateContainerVariant;
  children: TemplateContainerChild[];
}

export interface TemplateRowNode extends TemplateBaseNode {
  type: 'row';
  gap: RowGap;
  /** Rows hold ONLY columns — sum of child spans ≤ 12 (Zod refine). */
  children: TemplateColumnNode[];
}

export interface TemplateColumnNode extends TemplateBaseNode {
  type: 'column';
  span: ColumnSpan;
  children: TemplateContainerChild[];
}

export interface ItemPlaceholderNode extends TemplateBaseNode {
  type: 'item';
  /** Optional CSS-class hint for the public renderer (e.g. 'hero',
   *  'thumb', 'featured'). Free-text; max 40 chars. */
  variant?: string;
}

export type TemplateContainerChild = TemplateContainerNode | TemplateRowNode | ItemPlaceholderNode;

export type TemplateNode =
  | TemplateContainerNode
  | TemplateRowNode
  | TemplateColumnNode
  | ItemPlaceholderNode;

export interface LayoutTemplateDefinition {
  version: 1;
  /** Root is always a Container; admin cannot delete it. Stable id 'root'. */
  root: TemplateContainerNode;
}

/* ---------- Defaults ---------- */

export const EMPTY_TEMPLATE_ROOT: TemplateContainerNode = {
  id: 'root',
  type: 'container',
  variant: 'fluid',
  children: [],
};

export const EMPTY_LAYOUT_TEMPLATE_DEFINITION: LayoutTemplateDefinition = {
  version: 1,
  root: EMPTY_TEMPLATE_ROOT,
};

export const LAYOUT_TEMPLATE_COUNT_MIN = 1;
export const LAYOUT_TEMPLATE_COUNT_MAX = 50;

/* ---------- Admin shapes ---------- */

export interface LayoutTemplateSummary {
  id: string;
  slug: string;
  name: string;
  description: string | null;
  minCount: number;
  maxCount: number;
  /** Tree-walk count of ItemPlaceholder leaves; computed at list time. */
  itemCount: number;
  createdAt: string;
  updatedAt: string;
}

export interface LayoutTemplateDetail extends LayoutTemplateSummary {
  definition: LayoutTemplateDefinition;
  createdById: string | null;
  updatedById: string | null;
}

export interface LayoutTemplateListResponse {
  items: LayoutTemplateSummary[];
  total: number;
  page: number;
  pageSize: number;
}

/* ---------- Hydrated shape (used by HydratedSlotNode.layoutTemplate) ---------- */

export interface HydratedItemPlaceholderNode extends TemplateBaseNode {
  type: 'item';
  variant?: string;
  /** Item assigned to this position in tree order; null when the group
   *  doesn't provide enough items to fill the template. */
  item: HydratedGroupItem | null;
}

export interface HydratedTemplateContainerNode extends TemplateBaseNode {
  type: 'container';
  variant: TemplateContainerVariant;
  children: HydratedTemplateContainerChild[];
}

export interface HydratedTemplateRowNode extends TemplateBaseNode {
  type: 'row';
  gap: RowGap;
  children: HydratedTemplateColumnNode[];
}

export interface HydratedTemplateColumnNode extends TemplateBaseNode {
  type: 'column';
  span: ColumnSpan;
  children: HydratedTemplateContainerChild[];
}

export type HydratedTemplateContainerChild =
  | HydratedTemplateContainerNode
  | HydratedTemplateRowNode
  | HydratedItemPlaceholderNode;

export type HydratedTemplateNode =
  | HydratedTemplateContainerNode
  | HydratedTemplateRowNode
  | HydratedTemplateColumnNode
  | HydratedItemPlaceholderNode;

/** Compact template metadata bundled into HydratedSlotNode so the public
 *  renderer can size the slot and apply the template definition. */
export interface HydratedLayoutTemplate {
  slug: string;
  name: string;
  minCount: number;
  maxCount: number;
  root: HydratedTemplateContainerNode;
}
